// biome-ignore-all lint/correctness/useUniqueElementIds: historic
import { BlinqLogo } from '@components/BlinqAssets';
import Button from '@components/Button/Button';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import {
  type FC,
  type MouseEventHandler,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

const StepProgressBar = dynamic(() => import('@components/StepProgressBar/StepProgressBar'), {
  ssr: false,
});
const Step = dynamic(
  () => {
    // https://github.com/vercel/next.js/issues/4515#issuecomment-810635574
    const promise = import('@components/StepProgressBar/StepProgressBar').then((mod) => mod.Step);
    return promise;
  },
  {
    ssr: false,
  },
);

import { useUser } from '@context/user-context';
import useAnonymousUser from '@hooks/use-anonymous-user';
import useIntersection from '@hooks/use-intersection';
import useMedia from '@hooks/use-media';
import { getSignUpType, isIdentityBelongToAnonUser, setSignUpType } from '@modules/auth-helpers';
import { DEFAULT_CARD_COLOR } from '@modules/card-helpers';
import { hexToRGB } from '@modules/color-helpers';
import { noop } from '@modules/helpers';

import styles from './OnboardingLayout.module.scss';

interface OnboardingLayoutProps {
  title: string;
  cardColor?: string;
  children: ReactNode;
}

const OnboardingLayout: FC<OnboardingLayoutProps> = ({ children, title, cardColor }) => {
  const rgbColor = hexToRGB(cardColor ?? DEFAULT_CARD_COLOR, { tripletOnly: true });
  const pageTitle = makePageTitle(title);
  return (
    <div className={styles['onboarding-layout']} style={{ '--card-color': rgbColor }}>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      {children}
    </div>
  );
};

interface NewOnboardingLayoutProps {
  title: string;
}

const TwoSidedOnboardingLayout: FC<PropsWithChildren<NewOnboardingLayoutProps>> = ({
  children,
  title,
}) => {
  const pageTitle = makePageTitle(title);

  return (
    <div className={styles['new-onboarding-layout']}>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      {children}
    </div>
  );
};

type OnboardingSteps =
  | 'personal-details'
  | 'customize-card'
  | 'signup'
  | 'get-blinq'
  | 'finalise-payment'
  | 'company-details';
type AccessorySteps = 'customize-card' | 'get-blinq';

type NavigationSectionProps = {
  onNavigate?: MouseEventHandler<HTMLButtonElement> | undefined;
} & (
  | {
      flow: 'card';
      currentStep: OnboardingSteps;
    }
  | {
      flow: 'accessory';
      currentStep: AccessorySteps;
    }
);

interface CardFlowNavigationProps {
  onNavigate?: MouseEventHandler<HTMLButtonElement> | undefined;
  currentStep: OnboardingSteps;
}

interface AccessoryFlowNavigationProps {
  onNavigate?: MouseEventHandler<HTMLButtonElement> | undefined;
  currentStep: AccessorySteps;
}

const NavigationSectionContainer: FC<PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['navigation-section']}>{children}</div>;
};

const NavigationSection: FC<PropsWithChildren<NavigationSectionProps>> = (props) => {
  return (
    <NavigationSectionContainer>
      {props.flow === 'card' && (
        <CardFlowNavigation currentStep={props.currentStep} onNavigate={props.onNavigate} />
      )}
      {props.flow === 'accessory' && (
        <AccessoryFlowNavigation currentStep={props.currentStep} onNavigate={props.onNavigate} />
      )}
    </NavigationSectionContainer>
  );
};

const CardFlowNavigation: FC<PropsWithChildren<CardFlowNavigationProps>> = ({
  currentStep,
  onNavigate,
}) => {
  const screenSizeCategory = useMedia<'small' | 'large'>(
    [`(min-width: ${701}px)`],
    ['large'],
    'small',
  );
  const anonymousUser = useAnonymousUser();
  const { user, identity } = useUser();
  const [isAnonymous, setIsAnonymous] = useState(true);

  const { isInUpgradeSession } = usePremiumUpgradeFlow();

  useEffect(() => {
    const signUpType = getSignUpType();
    if (signUpType !== undefined) {
      setIsAnonymous(signUpType);
    }
  }, []);

  useEffect(() => {
    if (getSignUpType() === undefined && identity) {
      setSignUpType(isIdentityBelongToAnonUser(identity));
      setIsAnonymous(isIdentityBelongToAnonUser(identity));
    }
  }, [identity]);

  return (
    <StepProgressBar
      currentStep={currentStep}
      disableNextSteps={user?.isAnonymous || !!anonymousUser}
      onClick={onNavigate}
    >
      {screenSizeCategory === 'small' && (
        <Step
          id="personal-details"
          label="Personal details"
          href="/onboarding/personal-details?source=onboarding"
        />
      )}
      {screenSizeCategory === 'large' || currentStep === 'customize-card' ? (
        <Step id="customize-card" label="Customize your card" href="/onboarding/customize-card" />
      ) : (
        <Step id="company-details" label="Company Details" href="/onboarding/company-details" />
      )}
      {isAnonymous && (
        <Step
          id="signup"
          label="Create an account"
          href="/onboarding/signup"
          completed={user?.isAnonymous === false}
          disable={user?.isAnonymous === false}
        />
      )}

      {(() => {
        // The legacy `/onboarding/get-blinq` page has been removed by the
        // rebrand. The rebrand customize-card terminates at either the
        // signup page (anonymous users) or the user's dashboard
        // (logged-in users), so point the final step bubble at whichever
        // is the real "next" destination for this user.
        const finalHref = user?.userId
          ? `/${user.userId}/cards/personal?welcomeApp=1`
          : '/onboarding/signup';
        return isInUpgradeSession ? (
          <Step id="finalise-payment" label="Finalize Payment" href={finalHref} disable={true} />
        ) : (
          <Step id="get-blinq" label="Get your card" href={finalHref} />
        );
      })()}
    </StepProgressBar>
  );
};

const AccessoryFlowNavigation: FC<PropsWithChildren<AccessoryFlowNavigationProps>> = ({
  currentStep,
  onNavigate,
}) => {
  return (
    <StepProgressBar currentStep={currentStep} onClick={onNavigate}>
      <Step
        id="customize-card"
        label="Customize your card"
        href="/onboarding/accessory/customize-card"
      />
      <Step id="get-blinq" label="Get Blinq app" href="/onboarding/accessory/get-blinq" />
    </StepProgressBar>
  );
};

const MainSection = ({
  ref: forwardedRef,
  children,
}: { children: ReactNode } & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <div className={styles['main-section']} ref={forwardedRef}>
      {children}
    </div>
  );
};

MainSection.displayName = 'MainSection';

const MainSectionContents: FC<PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['main-section-contents']}>{children}</div>;
};

interface MainSectionHeaderProps {
  title: string;
  explainer?: ReactNode;
}

const MainSectionHeader: FC<PropsWithChildren<MainSectionHeaderProps>> = ({ title, explainer }) => (
  <div className={styles['main-section-header']}>
    <h1 className={styles['main-section-header-heading']}>{title}</h1>
    {explainer && <p className={styles['main-section-header-explainer']}>{explainer}</p>}
  </div>
);

const SideSection: FC<PropsWithChildren<{ theme: 'light' | 'dark'; allowOverflow?: boolean }>> = ({
  theme,
  allowOverflow = true,
  children,
}) => {
  const [isCentered, setIsCentered] = useState(false);

  const onIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    const contents = entries[0];
    setIsCentered(contents.isIntersecting);
  }, []);

  const [rootRef, contentsRef] = useIntersection(onIntersect, { threshold: 1 });

  return (
    <div
      className={styles['side-section']}
      data-centered={isCentered}
      data-theme={theme}
      data-allow-overflow={allowOverflow}
    >
      <div className={styles['blinq-logo-container']}>
        <BlinqLogo className={styles['blinq-logo']} />
      </div>
      <div className={styles['side-section-bounds']} ref={rootRef}>
        <div className={styles['side-section-content']} ref={contentsRef}>
          {children}
        </div>
      </div>
    </div>
  );
};

const OnboardingSideSection: FC<PropsWithChildren<{ allowOverflow?: boolean }>> = ({
  allowOverflow = true,
  children,
}) => {
  const [isCentered, setIsCentered] = useState(false);

  const onIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    const contents = entries[0];
    setIsCentered(contents.isIntersecting);
  }, []);

  const [rootRef, contentsRef] = useIntersection(onIntersect, { threshold: 1 });

  return (
    <div
      className={styles['new-side-section']}
      data-centered={isCentered}
      data-allow-overflow={allowOverflow}
    >
      <div className={styles['side-section-bounds']} ref={rootRef}>
        <div className={styles['side-section-content']} ref={contentsRef}>
          {children}
        </div>
      </div>
    </div>
  );
};

const SideSectionHeader: FC<PropsWithChildren<unknown>> = ({ children }) => (
  <h2 className={styles['side-section-header']}>{children}</h2>
);

const NavigationButtonsSection: FC<PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['navigation-buttons-section']}>{children}</div>;
};

interface BackButtonProps {
  onClick: MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
}

const BackButton: FC<PropsWithChildren<BackButtonProps>> = ({ children, onClick, loading }) => {
  const buttonMode = useMedia<'tiny' | 'medium'>([`(max-width: 350px)`], ['tiny'], 'medium');

  return (
    <div className={styles['back-button']}>
      <Button variant="text" size={buttonMode} onClick={onClick} loading={loading}>
        {children}
      </Button>
    </div>
  );
};

interface ForwardButtonProps {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
}

const ForwardButton: FC<PropsWithChildren<ForwardButtonProps>> = ({
  children,
  onClick,
  loading,
  fullWidth = false,
  disabled = false,
}) => (
  <div className={styles['forward-button']} data-full-width={fullWidth}>
    <Button
      variant="primary"
      size="medium"
      submit
      loading={loading}
      onClick={onClick ?? noop}
      disabled={disabled}
    >
      {children}
    </Button>
  </div>
);

import Tooltip, { centeredAbove } from '@components/Tooltip/Tooltip';
import { makePageTitle } from '@modules/page-title';
import { usePremiumUpgradeFlow } from 'pages/onboarding/upgrade-flow-selection';

// Specifically for the use case of the customize card screen in the onboarding flow
const CustomizeCardForwardButton: FC<PropsWithChildren<ForwardButtonProps>> = ({
  children,
  onClick,
  loading,
  fullWidth = false,
  disabled = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <div
      className={styles['forward-button']}
      data-full-width={fullWidth}
      onMouseEnter={() => {
        if (disabled) {
          setShowTooltip(true);
        }
      }}
    >
      <Tooltip
        size="small"
        label={'Add one or more details to continue'}
        aria-label="Save button disabled message"
        show={showTooltip}
        showTriangle={false}
        positioner={centeredAbove}
      >
        <div>
          <Button
            variant="primary"
            size="medium"
            submit
            loading={loading}
            onClick={onClick ?? noop}
            disabled={disabled}
          >
            {children}
          </Button>
        </div>
      </Tooltip>
    </div>
  );
};

export default OnboardingLayout;
export {
  BackButton,
  CustomizeCardForwardButton,
  ForwardButton,
  MainSection,
  MainSectionContents,
  MainSectionHeader,
  NavigationButtonsSection,
  NavigationSection,
  NavigationSectionContainer,
  OnboardingSideSection,
  SideSection,
  SideSectionHeader,
  TwoSidedOnboardingLayout,
};
